import { bind } from '@react-rxjs/core'
import { createSignal } from '@react-rxjs/utils'
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  filter,
  map,
  Observable,
  of,
  startWith,
  switchMap,
  take,
} from 'rxjs'
import { fromFetch } from 'rxjs/fetch'

import { dispensaryBySlugQuery } from '../../../groq/sanity-dispensaries/dispensary'
import { SanityDispensaryDocument } from '../../../groq/sanity-dispensaries/types'
import { isNotNull } from '../../../utils/collectionTools'
import { CustomError } from '../../../utils/errorTools'
import { coerceCaughtToLeft } from '../../../utils/rx/errors'
import { switchMapRight, tapRight } from '../../../utils/rx/operators'
import { latestAccessToken$ } from '../../auth/state'
import { fetchResponseToJsonResult, FetchResult, headers } from '../../fetch'
import {
  DeleteDispensaryInputData,
  PostDispensaryInputData,
  PutDispensaryInputData,
} from '../../sanity/types'
import { dispensariesClientLive, fetchQuery } from '../client'
import { latestDispensarySlug$ } from '../state'

// use BehaviorSubject with initial value to start observables
const dispensaryTickUpdate$ = new BehaviorSubject<void>(void 0)

const [updateDispensaryInput$, setUpdateDispensaryInput] = createSignal<
  | { method: 'PUT'; input: Omit<PutDispensaryInputData, 'slug'> }
  | { method: 'DELETE'; input: Omit<DeleteDispensaryInputData, 'slug'> }
>()
export { setUpdateDispensaryInput }
export { updateDispensaryInput$ }

class NewDispensaryNotFoundError extends CustomError {
  override name = `NewDispensaryNotFoundError`
  public readonly slug: string
  constructor(slug: string) {
    super(`Dispensary not found after creating`)
    this.slug = slug
  }
}

const fromDispensaryCreateRequest = (body: PostDispensaryInputData) =>
  latestAccessToken$.pipe(
    filter(isNotNull),
    take(1),
    switchMap((accessToken) =>
      fromFetch('/api/sanity-dispensaries/dispensary', {
        body: JSON.stringify(body),
        method: 'POST',
        headers: {
          ...headers.acceptJson,
          ...headers.authorization(accessToken),
          ...headers.contentTypeJson,
        },
      }),
    ),
    switchMap(fetchResponseToJsonResult),
    catchError(coerceCaughtToLeft),
  )

export const [useSanityDispensaryGetOrCreate, sanityDispensaryGetOrCreate$] = bind(
  latestDispensarySlug$.pipe(
    filter(isNotNull),
    switchMap((slug) => {
      const fetchDispensary = () =>
        fetchQuery(dispensaryBySlugQuery, {
          slug,
        })(dispensariesClientLive)
      return dispensaryTickUpdate$.pipe(
        // refetch with `dispensaryTickUpdate$` changes
        switchMap(() => fetchDispensary()),
        switchMapRight(({ data }) =>
          data
            ? // return existing Sanity dispensary
              of({ _tag: 'Right' as const, data })
            : // create dispensary in Sanity
              fromDispensaryCreateRequest({ slug }).pipe(
                // fetch dispensary after creation
                switchMapRight(() => fetchDispensary()),
                switchMapRight(({ data: createData }) => {
                  if (!createData) {
                    const error = new NewDispensaryNotFoundError(slug)
                    return of({ _tag: 'Left' as const, error })
                  }
                  return of({ _tag: 'Right' as const, data: createData })
                }),
              ),
        ),
      )
    }),
  ),
)

const fromDispensaryUpdateRequest = ({
  body,
  method,
}:
  | { method: 'PUT'; body: PutDispensaryInputData }
  | { method: 'DELETE'; body: DeleteDispensaryInputData }): Observable<
  FetchResult<SanityDispensaryDocument>
> =>
  latestAccessToken$.pipe(
    filter(isNotNull),
    take(1),
    switchMap((accessToken) =>
      fromFetch('/api/sanity-dispensaries/dispensary', {
        body: JSON.stringify(body),
        method,
        headers: {
          ...headers.acceptJson,
          ...headers.authorization(accessToken),
          ...headers.contentTypeJson,
        },
      }),
    ),
    switchMap((response) => fetchResponseToJsonResult<SanityDispensaryDocument>(response)),
    catchError(coerceCaughtToLeft),
  )

export const dispensaryUpdateFactory: (
  slug: string,
  updateInput:
    | { method: 'PUT'; input: Omit<PutDispensaryInputData, 'slug'> }
    | { method: 'DELETE'; input: Omit<DeleteDispensaryInputData, 'slug'> },
) => Observable<FetchResult<SanityDispensaryDocument>> = (slug, updateInput) =>
  fromDispensaryUpdateRequest(
    updateInput.method === 'PUT'
      ? { method: updateInput.method, body: { ...updateInput.input, slug } }
      : {
          method: updateInput.method,
          body: {
            ...updateInput.input,
            slug,
          },
        },
  ).pipe(
    tapRight(() => {
      dispensaryTickUpdate$.next()
    }),
  )

export const [useSanityDispensaryUpdate, sanityDispensaryUpdate$] = bind<{
  loading: boolean
  result: FetchResult<SanityDispensaryDocument> | undefined
}>(
  combineLatest([latestDispensarySlug$.pipe(filter(isNotNull)), updateDispensaryInput$]).pipe(
    switchMap(([slug, updateInput]) =>
      dispensaryUpdateFactory(slug, updateInput).pipe(
        map((result) => ({
          loading: false,
          result,
        })),
        startWith({
          loading: true,
          result: undefined,
        }),
      ),
    ),
  ),
  {
    loading: false,
    result: undefined,
  },
)
